'use client';

import { EmblaCarouselType } from 'embla-carousel';
import { useCallback, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { PageBlock } from '@/payload/payload-types-block';
import { UiBannerPicture } from '@/ui/banner-picture';
import { UiLink } from '@/ui/link';
import { UiKitCarousel } from '@/ui-kit/carousel';
import { UiKitTextCopy } from '@/ui-kit/text-copy';
import { UiToolSliderArrowCursor } from '@/ui-tool/slider-arrow-cursor';
import { UiToolSliderFading } from '@/ui-tool/slider-fading';
import {
  getBannerTextClassName,
  getBannerTextClassNameWithFallback
} from '@/util/banner-text-style';

import { styles } from './UiBannerCarouselBase.styles';

type BannerBase = PageBlock<'bannerBase'>;
type TextAlign = NonNullable<
  NonNullable<BannerBase['slides'][number]['contentStyle']>['contentAlign']
>;
type ButtonAlign = 'left' | 'center' | 'right';
const buttonTextAlignClassName: Record<ButtonAlign, string> = {
  left: 'w-full justify-center text-center',
  center: 'w-full justify-center text-center',
  right: 'w-full justify-center text-center'
};

export interface UiBannerCarouselBaseProps {
  banners: BannerBase['slides'];
  location?: BannerBase['location'];
  className?: string;
  onLinkClick?: (label?: string, skipMenuClick?: boolean) => void;
  onSlidesChange?: (index: number) => void;
  onBannerClick?: (index: number) => boolean | void;
  onCarouselInit?: (api: EmblaCarouselType) => void;
  bannerRef?: (index: number, element: HTMLElement | null) => void;
}

export function UiBannerCarouselBase({
  banners,
  location = 'default',
  className,
  onLinkClick,
  onSlidesChange,
  onBannerClick,
  onCarouselInit,
  bannerRef
}: UiBannerCarouselBaseProps) {
  const slots = styles({ location: location ?? 'default' });
  const [emblaApi, setEmblaApi] = useState<EmblaCarouselType>();

  const handleNavigate = useCallback(
    (direction: 'left' | 'right') => {
      if (!emblaApi) {
        return;
      }

      if (direction === 'left') {
        emblaApi.scrollPrev();
      } else {
        emblaApi.scrollNext();
      }

      emblaApi.plugins()?.autoplay?.reset();
    },
    [emblaApi]
  );

  const handleCarouselInit = useCallback(
    (api: EmblaCarouselType) => {
      setEmblaApi(api);
      onCarouselInit?.(api);
    },
    [onCarouselInit]
  );

  return (
    <UiToolSliderArrowCursor
      enabled={banners.length > 1}
      navigateOnClick
      onNavigate={handleNavigate}
    >
      <UiKitCarousel
        options={{ active: banners.length > 1, watchDrag: true, inViewThreshold: 0.5 }}
        className={slots.carousel({ className })}
        dotsClass={slots.dots()}
        dotClass={slots.dot()}
        activeDotClass={slots.activeDot()}
        onInit={handleCarouselInit}
        autoplay
        showDots
        showArrows={false}
        fade
        onSlidesChange={onSlidesChange}
      >
        {banners.map((banner, index) => {
          const textAlign = (banner.contentStyle?.contentAlign ?? 'center') as TextAlign;
          const buttonAlign = (banner.contentStyle?.buttonAlign ??
            (textAlign === 'left'
              ? 'left'
              : textAlign === 'right'
                ? 'right'
                : 'center')) as ButtonAlign;

          return (
            <div
              key={banner.id}
              ref={(element) => bannerRef?.(index, element)}
              className={slots.banner()}
            >
              {typeof banner.image !== 'number' && banner.image.sizes && (
                <UiBannerPicture
                  sizes={banner.image.sizes}
                  customSizes={banner.image.customSizes}
                  alt={banner.image.alt}
                  desktopFieldName="banner-base-desktop"
                  mobileFieldName="banner-base-mobile"
                  className={slots.picture()}
                />
              )}

              <div
                className={slots.content({ mode: banner.theme ?? 'light', textAlign })}
              >
                <div className={slots.upperTextWrapper()}>
                  <UiToolSliderFading
                    className={twMerge(
                      slots.upperText(),
                      getBannerTextClassName(banner.contentStyle?.upperText)
                    )}
                    containerClassName={slots.upperTextContainer()}
                  >
                    {banner.upperText?.map(({ id, value }) => (
                      <span
                        key={id}
                        className={slots.upperTextItem()}
                      >
                        {value}
                      </span>
                    ))}
                  </UiToolSliderFading>
                </div>

                <div className={slots.contentCenter({ textAlign })}>
                  <div
                    className={slots.title({
                      underline: Boolean(banner.title?.underline),
                      textAlign
                    })}
                  >
                    {banner.title?.firstLine && (
                      <span
                        className={twMerge(
                          slots.titleFirstLine(),
                          getBannerTextClassName(banner.contentStyle?.titleFirstLine)
                        )}
                      >
                        {banner.title.firstLine}
                      </span>
                    )}

                    {banner.title?.secondLine && (
                      <span
                        className={twMerge(
                          getBannerTextClassNameWithFallback(
                            location === 'product'
                              ? {
                                  fontFamily: 'chiron',
                                  fontSize: 'italic-m',
                                  fontSizeDesktop: '2xl'
                                }
                              : location === 'menu'
                                ? {
                                    fontFamily: 'chiron',
                                    fontSize: 'italic-m',
                                    fontSizeDesktop: '2xl-desk'
                                  }
                                : {
                                    fontFamily: 'chiron',
                                    fontSize: 'italic-l',
                                    fontSizeDesktop: 'italic-xl'
                                  },
                            banner.contentStyle?.titleSecondLine
                          )
                        )}
                      >
                        {banner.title.secondLine}
                      </span>
                    )}
                  </div>

                  {banner.description && (
                    <p
                      className={twMerge(
                        slots.description({ textAlign }),
                        getBannerTextClassName(banner.contentStyle?.description)
                      )}
                    >
                      {banner.description}
                    </p>
                  )}

                  {banner.promoCode && (
                    <UiKitTextCopy
                      bgColor="disabled"
                      text={banner.promoCode}
                    />
                  )}

                  {banner.button?.link?.url && (
                    <UiLink
                      link={banner.button.link}
                      buttonColor={banner.button.theme ?? 'primary'}
                      className={slots.button({ buttonAlign })}
                      childrenClassName={twMerge(
                        buttonTextAlignClassName[buttonAlign],
                        getBannerTextClassName(banner.contentStyle?.buttonLabel)
                      )}
                      showIcon={location === 'menu'}
                      onClick={() => {
                        const sentPromotion = onBannerClick?.(index);
                        onLinkClick?.(
                          [banner.title?.firstLine, banner.title?.secondLine]
                            .map((item) => item?.trim())
                            .filter(Boolean)
                            .join(' ') ||
                            banner.button?.link?.label ||
                            undefined,
                          Boolean(sentPromotion)
                        );
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </UiKitCarousel>
    </UiToolSliderArrowCursor>
  );
}
